/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  layerSelectors,
  layerActions,
  serviceSelectors,
  uiTypes,
  useSetupDialog,
  layerTypes,
} from '@opengeoweb/store';
import { LayerInfoDialog } from '@opengeoweb/webmap-react';

interface LayerInfoDialogConnectProps {
  source: uiTypes.Source;
  mapId: string;
}

export const LayerInfoDialogConnect: React.FC<LayerInfoDialogConnectProps> = ({
  source,
  mapId,
}) => {
  const dispatch = useDispatch();
  const layerInfo = useSelector((store: CoreAppStore) =>
    layerSelectors.getActiveLayerInfo(store),
  );

  const layer = useSelector((store: CoreAppStore) =>
    serviceSelectors.getLayerFromService(
      store,
      layerInfo?.serviceUrl || '',
      layerInfo?.layerName!,
    ),
  );

  const currentService = useSelector((store: CoreAppStore) =>
    serviceSelectors.getServiceByUrl(store, layerInfo?.serviceUrl || ''),
  );

  /* Get all layers on map and find id by serviceUrl and layerName */
  const getLayerId = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayersByMapId(store, mapId),
  ).find(
    (layer) =>
      layer.service === layerInfo?.serviceUrl &&
      layer.name === layerInfo?.layerName,
  )?.id;

  /* Get layerDetails with retrieved getLayerId */
  const layerDetails = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerById(store, getLayerId || ''),
  );

  /* Use layerDetails to toggle legend visibility */
  const onToggleLayerLegendVisibility = (showLayerLegend: boolean): void => {
    const layerId = layerDetails?.id || '';
    dispatch(
      layerActions.SetLayerLegendVisibilityPayload({
        layerId,
        showLayerLegend,
        mapId,
        origin: layerTypes.LayerActionOrigin.layerManager,
      }),
    );
  };

  /* Use layerDetails for showLegend value */
  const showLayerLegend =
    typeof layerDetails?.showLayerLegend === 'boolean'
      ? layerDetails.showLayerLegend
      : true;

  const { dialogOrder, onCloseDialog, setDialogOrder, isDialogOpen } =
    useSetupDialog(uiTypes.DialogTypes.LayerInfo);
  if (!layer) {
    return null;
  }
  return (
    <LayerInfoDialog
      isOpen={isDialogOpen}
      layer={layer}
      onClose={onCloseDialog}
      onMouseDown={setDialogOrder}
      onToggleLayerLegendVisibility={onToggleLayerLegendVisibility}
      order={dialogOrder}
      selectedStyle={layerInfo?.style}
      serviceName={currentService?.name!}
      serviceUrl={currentService?.serviceUrl}
      showLayerLegend={showLayerLegend}
      source={source}
    />
  );
};
